import { Box, Card, CardContent, Typography } from '@mui/material';
import { formatPercent, getAccuracyColor } from '../../theme/forecastStyles';
import type { StockHistorySummaryResponse } from '../../lib/api/stock-history';

interface SummaryStripProps {
  summary: StockHistorySummaryResponse | null;
  horizonLabel: string;
  formatDate: (dateStr: string | null) => string;
}

export default function SummaryStrip({ summary, horizonLabel, formatDate }: SummaryStripProps) {
  const hasData = summary && summary.total_signals > 0;
  const hasCompletedAny = summary && (summary.completed_7d ?? 0) > 0;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
      {/* Total Forecasts */}
      <Card sx={{ bgcolor: 'background.default', height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Total Forecasts
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {summary?.total_signals ?? 0}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {summary?.unique_symbols ?? 0} symbols tracked
          </Typography>
        </CardContent>
      </Card>

      {/* Windows Completed */}
      <Card sx={{ bgcolor: 'background.default', height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {horizonLabel} Windows Completed
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {summary?.completed_7d ?? 0}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {hasData
              ? `${summary!.total_signals ? ((summary!.completed_7d / summary!.total_signals) * 100).toFixed(0) : '0'}% of forecasts have full outcomes`
              : 'Waiting for live outcomes'}
          </Typography>
        </CardContent>
      </Card>

      {/* Forecast Accuracy */}
      <Card sx={{ bgcolor: 'background.default', height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Forecast Accuracy ({horizonLabel})
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: getAccuracyColor(summary?.accuracy_7d ?? null)
            }}
          >
            {formatPercent(summary?.accuracy_7d ?? null)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {hasCompletedAny
              ? `${horizonLabel} horizon based on completed outcomes`
              : `Populates as ${horizonLabel} windows complete`}
          </Typography>
        </CardContent>
      </Card>

      {/* Coverage Window */}
      <Card sx={{ bgcolor: 'background.default', height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Coverage Window
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {formatDate(summary?.first_date ?? null)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            to {formatDate(summary?.last_date ?? null)}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
